import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Trash2, Power, CheckCircle2, XCircle, Search, ShieldCheck, Clock,
} from "lucide-react";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  distributor: "Distributor",
  retailer: "Retailer",
  customer: "Customer",
};

const ROLE_COLOR = {
  super_admin: "bg-brand-blue/10 text-brand-blue border-brand-blue/20",
  distributor: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  retailer: "bg-brand-green/10 text-brand-green border-brand-green/20",
  customer: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

const TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending approval" },
  { id: "retailer", label: "Retailers" },
  { id: "distributor", label: "Distributors" },
  { id: "customer", label: "Customers" },
];

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/admin/users/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const toggle = async (uid) => {
    try {
      await api.patch(`/admin/users/${uid}/toggle`);
      toast.success("User updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const approve = async (uid) => {
    try {
      await api.patch(`/admin/users/${uid}/approve`);
      toast.success("Retailer approved & activated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const reject = async (uid) => {
    if (!window.confirm("Reject and suspend this retailer?")) return;
    try {
      await api.patch(`/admin/users/${uid}/reject`);
      toast.success("Retailer rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const remove = async (uid) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const money = (v) => "₹" + Number(v || 0).toFixed(2);

  const filtered = useMemo(() => {
    let list = users;
    if (tab === "pending") list = list.filter((u) => !u.approved && u.role !== "super_admin" && u.role !== "customer");
    else if (tab !== "all") list = list.filter((u) => u.role === tab);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((u) => (u.name || "").toLowerCase().includes(s) ||
                                (u.email || "").toLowerCase().includes(s) ||
                                (u.phone || "").toLowerCase().includes(s));
    }
    return list;
  }, [users, tab, q]);

  const pendingCount = users.filter((u) => !u.approved && u.role !== "super_admin" && u.role !== "customer").length;

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Admin</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Users & Retailer Approvals</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Approve retailer signups, manage roles, and control platform access.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TABS.map((t2) => (
              <TabsTrigger key={t2.id} value={t2.id} data-testid={`users-tab-${t2.id}`}>
                {t2.label}
                {t2.id === "pending" && pendingCount > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-white h-5 min-w-5 px-1.5 rounded-full">{pendingCount}</Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email or mobile" className="pl-8 w-full sm:w-72" data-testid="users-search" />
        </div>
      </div>

      <Card className="border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Wallet</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Approval</TableHead>
              <TableHead className="w-52">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => {
              const isPendingApproval = !u.approved && u.role !== "super_admin" && u.role !== "customer";
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell className="text-xs tabular-nums" data-testid={`user-phone-${u.id}`}>
                    {u.phone ? (
                      <a href={`tel:${u.phone}`} className="text-primary hover:underline">{u.phone}</a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === user.id}>
                      <SelectTrigger className="h-8 w-36" data-testid={`user-role-select-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(ROLE_LABEL).map((r) => (
                          <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{money(u.wallet_balance)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.active !== false ? "bg-brand-green/10 text-brand-green border-brand-green/20" : "bg-muted"}>
                      {u.active !== false ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {u.role === "super_admin" || u.role === "customer" ? (
                      <Badge variant="outline" className="bg-muted">n/a</Badge>
                    ) : isPendingApproval ? (
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                        <Clock size={12} className="mr-1" /> Pending
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/20">
                        <ShieldCheck size={12} className="mr-1" /> Approved
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {isPendingApproval && (
                        <>
                          <Button size="sm" className="h-8 bg-brand-green hover:bg-brand-green-hover text-white" onClick={() => approve(u.id)} data-testid={`user-approve-${u.id}`}>
                            <CheckCircle2 size={13} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={() => reject(u.id)} data-testid={`user-reject-${u.id}`}>
                            <XCircle size={13} />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => toggle(u.id)} title="Toggle active" className="h-8 w-8" data-testid={`user-toggle-${u.id}`}>
                        <Power size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(u.id)} disabled={u.id === user.id} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`user-delete-${u.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No users match this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
